import { useState, useEffect, useRef } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, Phone, Facebook, Radio, CreditCard, Cloud, UserPlus, Heart } from "lucide-react";
import { useNavigation } from "@/hooks/useNavigation";

const LOGO = "/logo.png";

// Fallback nav - used until the CMS-backed navigation loads. Kept in sync with
// the seed_cms_content() migration in backend/server.py.
const FALLBACK_NAV = [
  { to: "/", label: "Home" },
  {
    label: "About Us",
    to: "/about",
    children: [
      { to: "/about", label: "About Us" },
      { to: "/membership", label: "Become a Member" },
      { to: "/hiring", label: "We're Hiring" },
      { to: "/bulletin", label: "Temple Bulletin" },
      { to: "/rabbi", label: "Meet Our Rabbi" },
      { to: "/about/staff", label: "Meet Our Staff" },
      { to: "/about/president", label: "Message from Our President" },
      { to: "/about/board", label: "Meet Our Board" },
      { to: "/about/sisterhood", label: "Sisterhood" },
      { to: "/about/mens-club", label: "Men's Club" },
      { to: "/about/social-action", label: "Social Action" },
    ],
  },
  {
    label: "Worship",
    to: "/worship",
    children: [
      { to: "/worship/shabbat", label: "Shabbat" },
      { to: "/worship/holidays", label: "Holidays" },
      { to: "/worship/yahrzeit", label: "Yahrzeit" },
    ],
  },
  {
    label: "Education",
    to: "/education",
    children: [
      { to: "/education/adult", label: "Adult Education" },
      { to: "/education/youth", label: "Youth Education" },
      { to: "/education/path-to-judaism", label: "Path to Judaism" },
    ],
  },
  {
    label: "Calendar",
    to: "/calendar/weekly",
    children: [
      { to: "/calendar/weekly", label: "Weekly Calendar" },
      { to: "/events", label: "All Events" },
      { to: "/calendar/zoom", label: "Zoom Meetings" },
      { to: "/calendar/film-series", label: "Film Series 2026\u20132027" },
      { to: "/events/fallbingonight", label: "Fall Bingo Night - Nov 14th" },
    ],
  },
  { to: "/gallery", label: "Gallery" },
  { to: "/contact", label: "Contact" },
];

const SHULCLOUD_BASE = "https://tiob.shulcloud.com";

const UTILITY_LINKS = [
  { label: "ShulCloud", icon: Cloud, href: `${SHULCLOUD_BASE}/login.php`, external: true },
  { label: "Payments", icon: CreditCard, href: `${SHULCLOUD_BASE}/payment.php`, external: true },
  { label: "Streaming", icon: Radio, href: "https://www.facebook.com/TempleIsraelBrevardFL/live", external: true },
  { label: "New Members", icon: UserPlus, href: "/membership#apply", external: false, note: "by appointment" },
];

export default function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [mobileExpanded, setMobileExpanded] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const closeTimer = useRef(null);

  // Live nav from CMS with static fallback (never leaves the header empty)
  const { items: NAV } = useNavigation(FALLBACK_NAV);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setOpenDropdown(null);
    setMobileExpanded(null);
  }, [location.pathname]);

  const openMenu = (label) => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setOpenDropdown(label);
  };
  const scheduleClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpenDropdown(null), 180);
  };

  return (
    <>
      {/* TOP UTILITY BAR */}
      <div className="bg-[var(--blue-ink)] text-white text-xs" data-testid="top-bar">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 py-1.5 flex flex-wrap items-center justify-between gap-2 md:gap-3">
          <div className="flex items-center gap-1 md:gap-2">
            <a
              href="https://www.facebook.com/TempleIsraelBrevardFL"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-[var(--blue-bright)] inline-flex items-center justify-center min-w-[44px] min-h-[44px] -my-1"
              aria-label="Facebook"
              data-testid="topbar-facebook"
            >
              <Facebook size={16} />
            </a>
            <a
              href="tel:+13216319494"
              className="hover:text-[var(--blue-bright)] inline-flex items-center gap-1.5 font-medium min-h-[44px] -my-1 px-1"
              data-testid="topbar-phone"
            >
              <Phone size={14} /> (321) 631-9494
            </a>
          </div>
          <div className="flex flex-wrap items-center gap-1.5 md:gap-2">
            {UTILITY_LINKS.map((u) => (
              <UtilButton key={u.label} {...u} />
            ))}
            <Link
              to="/donate"
              className="inline-flex items-center gap-1.5 bg-[var(--honey)] text-[var(--blue-ink)] hover:bg-[#EFA72A] px-3 py-1.5 md:px-3.5 md:py-2 rounded-full font-semibold transition-colors min-h-[32px] md:min-h-[36px] text-xs md:text-xs"
              data-testid="topbar-donate"
            >
              <Heart size={14} /> Donate
            </Link>
          </div>
        </div>
      </div>

      {/* MAIN HEADER */}
      <header
        className={`sticky top-0 z-40 transition-all duration-300 bg-white border-b ${
          scrolled ? "border-[var(--line)] shadow-[0_4px_24px_-12px_rgba(15,95,170,0.18)]" : "border-transparent"
        }`}
        data-testid="site-header"
      >
        <div className="max-w-7xl mx-auto px-5 lg:px-10 py-3 flex items-center justify-between gap-6">
          <Link to="/" className="flex items-center gap-3" data-testid="logo-link" aria-label="Temple Israel of Brevard - Home">
            <img
              src={LOGO}
              alt="Temple Israel of Brevard"
              className="h-12 md:h-14 w-auto"
              loading="eager"
            />
          </Link>

          {/* DESKTOP NAV */}
          <nav className="hidden lg:flex items-center gap-1 text-[14px] font-medium" aria-label="Main navigation" data-testid="desktop-nav">
            {NAV.map((n) => {
              const isOpen = openDropdown === n.label;
              if (!n.children) {
                return (
                  <NavLink
                    key={n.label}
                    to={n.to}
                    end={n.to === "/"}
                    className={({ isActive }) =>
                      `px-3 py-2 rounded-md transition-colors ${
                        isActive ? "text-[var(--blue-deep)]" : "text-[var(--ink)] hover:text-[var(--blue-deep)]"
                      }`
                    }
                    data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
                  >
                    {n.label}
                  </NavLink>
                );
              }
              return (
                <div
                  key={n.label}
                  className="relative"
                  onMouseEnter={() => openMenu(n.label)}
                  onMouseLeave={scheduleClose}
                >
                  <button
                    type="button"
                    onClick={() => setOpenDropdown(isOpen ? null : n.label)}
                    aria-expanded={isOpen}
                    aria-haspopup="true"
                    className="inline-flex items-center gap-1 px-3 py-2 rounded-md text-[var(--ink)] hover:text-[var(--blue-deep)]"
                    data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
                  >
                    {n.label}
                    <ChevronDown size={14} className={`transition-transform ${isOpen ? "rotate-180" : ""}`} />
                  </button>
                  {isOpen && (
                    <div
                      className="absolute left-0 top-full pt-2 min-w-[240px] z-50"
                      onMouseEnter={() => openMenu(n.label)}
                      onMouseLeave={scheduleClose}
                    >
                      <div className="bg-white border border-[var(--line)] rounded-2xl shadow-[0_24px_40px_-16px_rgba(15,95,170,0.22)] py-2 overflow-hidden">
                        {n.children.map((c) => (
                          <Link
                            key={c.to}
                            to={c.to}
                            className="block px-4 py-2.5 text-sm text-[var(--ink)] hover:bg-[var(--blue)]/8 hover:text-[var(--blue-deep)]"
                            onClick={() => setOpenDropdown(null)}
                            data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}-${c.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                          >
                            {c.label}
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </nav>

          {/* DESKTOP DONATE */}
          <div className="hidden lg:flex items-center">
            <Link to="/donate" className="btn-primary text-sm" data-testid="donate-cta-header">
              <Heart size={14} /> Donate
            </Link>
          </div>

          {/* MOBILE TOGGLE */}
          <button
            type="button"
            className="lg:hidden text-[var(--blue-ink)] p-2 rounded-md hover:bg-[var(--line)]"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileOpen}
            data-testid="mobile-menu-toggle"
          >
            {mobileOpen ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>

        {/* MOBILE MENU */}
        {mobileOpen && (
          <div className="lg:hidden bg-white border-t border-[var(--line)] max-h-[80vh] overflow-y-auto" data-testid="mobile-menu">
            <nav className="px-5 py-3 flex flex-col" aria-label="Mobile navigation">
              {NAV.map((n) => {
                if (!n.children) {
                  return (
                    <NavLink
                      key={n.label}
                      to={n.to}
                      end={n.to === "/"}
                      className={({ isActive }) =>
                        `px-4 py-3 rounded-lg font-medium ${
                          isActive ? "bg-[var(--blue)]/10 text-[var(--blue-deep)]" : "text-[var(--ink)] hover:bg-[var(--line)]"
                        }`
                      }
                      data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
                    >
                      {n.label}
                    </NavLink>
                  );
                }
                const expanded = mobileExpanded === n.label;
                return (
                  <div key={n.label} className="border-b border-[var(--line)] last:border-0">
                    <button
                      type="button"
                      onClick={() => setMobileExpanded(expanded ? null : n.label)}
                      className="w-full flex items-center justify-between px-4 py-3 font-medium text-[var(--ink)]"
                      aria-expanded={expanded}
                      data-testid={`mobile-nav-toggle-${n.label.toLowerCase().replace(/\s/g, "-")}`}
                    >
                      {n.label}
                      <ChevronDown size={16} className={`transition-transform ${expanded ? "rotate-180" : ""}`} />
                    </button>
                    {expanded && (
                      <div className="pb-2">
                        {n.children.map((c) => (
                          <Link
                            key={c.to}
                            to={c.to}
                            className="block pl-8 pr-4 py-2 text-sm text-[var(--ink-soft)] hover:text-[var(--blue-deep)]"
                          >
                            {c.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
              <Link to="/donate" className="btn-primary mt-4 mb-2 justify-center" data-testid="mobile-donate-cta">
                <Heart size={14} /> Donate
              </Link>
              <div className="grid grid-cols-2 gap-2 pb-3">
                {UTILITY_LINKS.map((u) => (
                  <UtilButton key={u.label} {...u} mobile />
                ))}
              </div>
            </nav>
          </div>
        )}
      </header>

      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-50 focus:bg-[var(--blue)] focus:text-white focus:px-4 focus:py-2 focus:rounded"
      >
        Skip to main content
      </a>
    </>
  );
}

function UtilButton({ label, icon: Icon, href, external, mobile }) {
  const cls = mobile
    ? "inline-flex items-center justify-center gap-1.5 bg-[var(--cream)] border border-[var(--line-warm)] text-[var(--blue-ink)] hover:bg-[var(--blue)]/8 px-3 py-2 rounded-lg text-xs font-medium min-h-[44px]"
    : "hidden md:inline-flex items-center gap-1.5 bg-white/10 hover:bg-white/20 text-white px-3 py-2 rounded-full text-xs uppercase tracking-wider font-medium transition-colors min-h-[36px]";
  if (external) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" className={cls} data-testid={`util-${label.toLowerCase().replace(/\s/g, "-")}`}>
        <Icon size={12} /> {label}
      </a>
    );
  }
  return (
    <Link to={href} className={cls} data-testid={`util-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <Icon size={12} /> {label}
    </Link>
  );
}
